import React from 'react'
import { useTranslation } from 'react-i18next'
import { ListPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoney } from '../lib/money'
import type { CostInvoice, CostLineView } from '../types'

interface Props { invoice: CostInvoice; lines: CostLineView[]; currency: string; locale: string; canEdit: boolean; onEdit: () => void }

const InvoiceLinesCard: React.FC<Props> = ({ invoice, lines, currency, locale, canEdit, onEdit }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_invoice_lines_title')}</CardTitle>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <ListPlus className="h-4 w-4" />
            {t('CostControl_invoice_lines_edit')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('CostControl_lines_item')}</TableHead>
              <TableHead className="w-44 text-right">{t('CostControl_lines_amount')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((l) => (
              <TableRow key={l.id}>
                <TableCell>
                  {l.path && <div className="text-xs text-muted-foreground">{l.path}</div>}
                  <div>{l.nodeId === null ? t('CostControl_unallocated') : l.nodeCode ? `${l.nodeCode} · ${l.nodeName}` : l.nodeName}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{money(l.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-semibold">{t('CostControl_invoice_lines_total')}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{money(invoice.amount)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </Card>
  )
}
export default InvoiceLinesCard
